'use client';

import { FC, ReactNode, ReactElement, useEffect, useMemo, useRef } from 'react';
import { easeInOut, motion } from 'framer-motion';
import { css } from '@bts-web/utils-style-engine';
import { NavigationBar } from '../NavigationBar/NavigationBar';
import { CloseDrawerButton } from './CloseDrawerButton';
import { FilledDismiss } from '../../svgIcons';
import { useScrollLock } from '../../utils/useScrollLock';
import { useDrawerMetadata } from '../ModalDrawerContext/ModalDrawerContext';

type TitleType = ReactElement | string;

interface DrawerProps {
  children: ReactNode;
  title?: TitleType;
  hideDefaultHeader?: boolean;
  showBorderBottom?: boolean;
}

const DrawerHeader: FC<{
  title?: TitleType;
  disableCloseButton?: boolean;
  isBottomDrawer: boolean;
  showBorderBottom?: boolean;
}> = ({
  title,
  disableCloseButton = false,
  isBottomDrawer,
  showBorderBottom,
}) => {
  const hideCloseButton = disableCloseButton;

  const renderTitle = useMemo(() => {
    return title || <div className={css({ flex: 1, display: 'flex' })} />;
  }, [isBottomDrawer, title]);

  return (
    <>
      {/* - top gray bar for mobile bottom drawer - */}
      <div
        className={css({
          display: {
            base: 'flex',
            lg: 'none',
          },
          justifyContent: 'center',
          pt: 'extra_small_2',
          pb: 'extra_small',
        })}
      >
        <span
          className={css({
            maxWidth: '40px',
            height: '2px',
            flex: 1,
            backgroundColor: 'neutrals.fill_tertiary',
            borderEndEndRadius: 'smallBottomRight',
            borderEndStartRadius: 'smallTopRight',
            borderStartStartRadius: 'smallTopLeft',
            borderStartEndRadius: 'smallBottomLeft',
            marginBottom: '4px',
          })}
        />
      </div>
      {/* --- */}

      <NavigationBar
        isTitleCentered
        showBorderBottom={isBottomDrawer || showBorderBottom}
        className={
          isBottomDrawer
            ? css({
                '&[data-element="navigation-bar"]': {
                  borderStartEndRadius: 'smallTopRight',
                  borderStartStartRadius: 'smallTopLeft',
                  ...(!title ? { minHeight: '24px' } : {}),
                },
              })
            : ''
        }
        title={renderTitle}
        {...(hideCloseButton
          ? {}
          : {
              NavigationRightSlot: (
                <CloseDrawerButton
                  icon={<FilledDismiss size={24} data-dir="rtl" />}
                />
              ),
            })}
      />
    </>
  );
};

const drawerVariants = {
  right: {
    initial: { x: '50%', opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: '50%', opacity: 0 },
  },
  bottom: {
    initial: { y: '50%', opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: '50%', opacity: 0 },
  },
};

const drawerTransition = {
  damping: 25,
  stiffness: 300,
  ease: easeInOut,
};

export const Drawer: FC<DrawerProps> = ({
  children,
  title,
  hideDefaultHeader = false,
  showBorderBottom = false,
}) => {
  const {
    drawerId: id,
    position,
    disableCloseButton,
    title: contextTitle,
  } = useDrawerMetadata();

  const currentVariants = drawerVariants[position];

  const localTitle = title ?? (contextTitle || '');

  const isBottomDrawer = position === 'bottom';

  useScrollLock(true);

  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    drawerRef.current?.focus();
  }, []);

  return (
    <motion.div
      ref={drawerRef}
      data-element={`drawer-${id}`}
      id={`drawer-${id}`}
      tabIndex={-1}
      initial="initial"
      animate="animate"
      exit="exit"
      variants={currentVariants}
      transition={drawerTransition}
      data-testid="drawer"
      data-position={position}
      className={css({
        display: 'flex',
        flexDirection: 'column',
        bg: 'screen_background.primary',
        maxHeight: '100vh',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        ...(position === 'right'
          ? {
              width: {
                base: '100%',
                lg: '420px',
              },
              height: '100vh',
            }
          : {}),
        ...(position === 'bottom'
          ? {
              borderTopRadius: '8px',
              height: 'auto',
              maxHeight: '90vh',
              width: '100%',
            }
          : {}),
      })}
    >
      {!hideDefaultHeader && (
        <DrawerHeader
          {...(localTitle ? { title: localTitle } : {})}
          disableCloseButton={disableCloseButton}
          isBottomDrawer={isBottomDrawer}
          showBorderBottom={showBorderBottom}
        />
      )}

      <div
        className={css({
          overflowY: 'auto',
          flex: 1,
          ...(isBottomDrawer ? { maxHeight: '80vh' } : {}),
        })}
      >
        {children}
      </div>
    </motion.div>
  );
};
